/**
 * EditCampaignModal Component
 *
 * Modal for editing campaign details including name, description, and status
 */

import { useState, useEffect } from "react";
import { FaFolderOpen, FaTimes, FaSpinner } from "react-icons/fa";
import { db, functions } from "../../firebase";
import { CampaignRepository } from "../../models/CampaignRepository";
import { useToast } from "./Toast";
import "./CreateMailshotModal.scss"; // Reuse the same styles

const CAMPAIGN_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export const EditCampaignModal = ({
  isOpen,
  campaign,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });
  const [loading, setLoading] = useState(false);

  // Initialize form data when campaign changes
  useEffect(() => {
    if (campaign && isOpen) {
      setFormData({
        name: campaign.name || "",
        description: campaign.description || "",
        status: campaign.status || "active",
      });
    }
  }, [campaign, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.warning("Please enter a campaign name");
      return;
    }

    setLoading(true);
    try {
      const campaignRepo = new CampaignRepository(db, functions);
      
      // Prepare updates
      const updates = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
      };

      await campaignRepo.update(campaign.id, updates);

      toast.success("Campaign updated successfully");

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error updating campaign:", error);
      if (error.code === "permission-denied") {
        toast.error("Permission denied: Only admins can edit campaigns.");
      } else {
        toast.error("Failed to update campaign: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !campaign) return null;

  return (
    <div className="create-mailshot-modal-overlay" onClick={handleClose}>
      <div
        className="create-mailshot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-mailshot-modal__header">
            <div className="create-mailshot-modal__title">
              <FaFolderOpen />
              Edit Campaign
            </div>
            <button
              type="button"
              className="create-mailshot-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-mailshot-modal__body">
            <div className="form-group">
              <label htmlFor="name">
                Campaign Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Q1 2026 Campaign"
                required
                disabled={loading}
                autoFocus
              />
              <span className="help-text">
                Give this campaign a descriptive name
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description <span style={{ color: "#6b7280" }}>(optional)</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add notes or details about this campaign..."
                disabled={loading}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">
                Status <span className="required">*</span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {CAMPAIGN_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <span className="help-text">
                Set to 'archived' to hide from active campaigns list
              </span>
            </div>
          </div>

          <div className="create-mailshot-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !formData.name.trim()}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
